import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import authStore from "../store/AuthStore";
import { generateToken } from "../services/AuthService";
import MainLayout from "../component/Layout/MainLayout";
import IGateReport from "../reports/iGateReport";
import ICourierReport from "../reports/iCourierReport";

function AppRoutes() {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const initialize = async () => {
      try {
        // LOAD SESSION
        authStore.loadSession();

        // URL PARAMS (for initial authentication only)
        const params = new URLSearchParams(window.location.search);
        const urlKey = params.get("key");
        const urlReportType = params.get("report_type");
        const savedKey = sessionStorage.getItem("jwtKey");
        const key = urlKey || savedKey;

        // SAVE KEY if provided in URL
        if (urlKey) {
          sessionStorage.setItem("jwtKey", urlKey);
        }

        // NO KEY - unauthorized
        if (!key) {
          setAuthorized(false);
          setLoading(false);
          return;
        }

        if (authStore.token) {
          setAuthorized(true);
          setLoading(false);

          // ONLY redirect if there's a report_type in URL
          if (urlReportType) {
            const cleanPath = `/${urlReportType.toLowerCase()}`;
            navigate(cleanPath, { replace: true });
          } else if (
            window.location.search.includes("key=") &&
            !urlReportType
          ) {
            window.history.replaceState({}, "", window.location.pathname);
          }
          return;
        }

        // DECODE JWT
        const decodedData = jwtDecode(decodeURIComponent(key));

        // SAVE AUTH
        authStore.setAuth(decodedData);

        // GENERATE TOKEN
        const tokenResponse = await generateToken(
          decodedData.url,
          decodedData.username,
          decodedData.password,
        );

        // INVALID TOKEN
        if (!tokenResponse) {
          authStore.clearAuth();
          setAuthorized(false);
          setLoading(false);
          return;
        }

        // SAVE TOKEN
        authStore.setToken(tokenResponse.token);
        setAuthorized(true);

        if (urlReportType) {
          // If report_type is provided, redirect to clean URL
          const cleanPath = `/${urlReportType.toLowerCase()}`;
          navigate(cleanPath, { replace: true });
        } else {
          // If no report_type, just clean the URL and stay on root
          window.history.replaceState({}, "", window.location.pathname);
        }
      } catch (error) {
        console.log("APP ROUTES ERROR :", error);
        authStore.clearAuth();
        setAuthorized(false);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [navigate]);

  // LOADING
  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  if (!authorized) {
    return (
      <div className="unauthorized-container">
        <h2> Unauthorized </h2>
      </div>
    );
  }

  // AUTHORIZED - Show routes with clean URLs
  return (
    <Routes>
      <Route path="/" element={<MainLayout />}>
        {/* Clean URLs without query parameters */}
        <Route path="igate" element={<IGateReport />} />
        <Route path="icourier" element={<ICourierReport />} />

        {/* Default route when no report is selected */}
        <Route index element={<NoReportSelected />} />
      </Route>

      {/* Redirect any unknown routes to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Component for when no report is selected
function NoReportSelected() {
  return (
    <div className="no-report-container">
      <img
        src={require("../assets/icons/not-found.png")}
        alt="No Report"
        className="no-report-icon"
      />
      <h2>No Report Selected</h2>
      <p style={{ marginTop: "20px", fontSize: "14px", color: "#666" }}></p>
    </div>
  );
}

export default AppRoutes;
